import React from 'react';
import '../assets/Orders.css';

interface OrderCardProps {
  orderId: string;
  orderNumber: string;
  storeName: string;
  customerName?: string;
  preferredDeliveryTime?: string;
  orderStatus: string;
  isSelected?: boolean;
  assignedDriver?: string;
  paymentStatus?: string;
  isReturn?: boolean;
  returnReason?: string;
  pickedUp?: boolean;
  /** Why this order cannot be routed. Set = the card is not selectable. */
  blockedReason?: string | null;
  onSelectionChange?: (orderNumber: string, isSelected: boolean) => void;
  onMarkDelivered?: (orderId: string) => void;
  onMarkPickedUp?: (orderId: string) => void;
}

const statusChip = (status: string, assignedDriver?: string) => {
  if (assignedDriver) return 'chip--brand';
  switch ((status || '').toLowerCase()) {
    case 'delivered':
    case 'completed': return 'chip--ok';
    case 'cancelled': return 'chip--danger';
    case 'in progress': return 'chip--info';
    default: return 'chip--neutral';
  }
};

const paymentChip = (status?: string) => {
  switch (status) {
    case 'Paid': return 'chip--ok';
    case 'Paid by Shop': return 'chip--gold';
    default: return 'chip--neutral';
  }
};

const WarnIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

export const OrderCard: React.FC<OrderCardProps> = ({
  orderId,
  orderNumber,
  storeName,
  customerName,
  preferredDeliveryTime,
  orderStatus,
  isSelected = false,
  assignedDriver,
  paymentStatus,
  isReturn = false,
  returnReason,
  pickedUp = false,
  blockedReason = null,
  onSelectionChange,
  onMarkDelivered,
  onMarkPickedUp,
}) => {
  const selectable = !blockedReason && !!onSelectionChange;

  const toggle = () => {
    if (selectable) onSelectionChange!(orderNumber, !isSelected);
  };

  return (
    <div
      className={`ocard ${isSelected ? 'is-selected' : ''} ${blockedReason ? 'is-blocked' : ''}`}
      onClick={toggle}
      role="group"
      aria-disabled={blockedReason ? true : undefined}
    >
      <div className="ocard__row">
        {onSelectionChange && (
          <input
            type="checkbox"
            className="ocard__check"
            checked={isSelected}
            disabled={!!blockedReason}
            onChange={e => onSelectionChange(orderNumber, e.target.checked)}
            onClick={e => e.stopPropagation()}
            aria-label={
              blockedReason
                ? `Order ${orderNumber} cannot be routed: ${blockedReason}`
                : `Select order ${orderNumber}`
            }
          />
        )}

        <div className="ocard__main">
          <div className="ocard__idline">
            {/* Order numbers are 6 digits now, but older rows are ORD-… — show whatever it is */}
            <span className="ocard__num mono">{orderNumber}</span>
            {isReturn && <span className="chip chip--warn">Return</span>}
            {pickedUp && <span className="chip chip--info">Picked up</span>}
          </div>
          <p className="ocard__shop">{storeName}</p>
          <p className="ocard__meta">
            {customerName && <span>{customerName}</span>}
            {preferredDeliveryTime && <span>{preferredDeliveryTime}</span>}
          </p>
          {returnReason && <p className="ocard__reason">{returnReason}</p>}
        </div>

        <div className="ocard__side">
          <span className={`chip ${statusChip(orderStatus, assignedDriver)}`}>
            {assignedDriver ? `Driver: ${assignedDriver}` : orderStatus}
          </span>
          {paymentStatus && (
            <span className={`chip ${paymentChip(paymentStatus)}`}>{paymentStatus}</span>
          )}
        </div>
      </div>

      {blockedReason && (
        <p className="ocard__blocked">
          <WarnIcon />
          {blockedReason}
        </p>
      )}

      {(onMarkPickedUp || onMarkDelivered) && orderStatus !== 'delivered' && !isReturn && (
        <div className="ocard__actions">
          {onMarkPickedUp && !pickedUp && (
            <button className="btn btn--secondary btn--sm"
                    onClick={e => { e.stopPropagation(); onMarkPickedUp(orderId); }}>
              Mark picked up
            </button>
          )}
          {onMarkDelivered && (
            <button className="btn btn--secondary btn--sm"
                    onClick={e => { e.stopPropagation(); onMarkDelivered(orderId); }}>
              Mark delivered
            </button>
          )}
        </div>
      )}
    </div>
  );
};
